import type { CartItem, PurchaseType } from '../../types'
import { formatRupiah } from '../../types'
import { CUSTOMER_TYPE_LABELS } from './customerTypeLabels'

interface CartDrawerProps {
  cart: CartItem[]
  total: number
  onClose: () => void
  onRemove: (productId: number, purchaseType: PurchaseType) => void
  onUpdateQuantity: (productId: number, purchaseType: PurchaseType, delta: number) => void
  onCheckout: () => void
  disabled?: boolean
  outletName: string
}

export function CartDrawer({
  cart,
  total,
  onClose,
  onRemove,
  onUpdateQuantity,
  onCheckout,
  disabled = false,
  outletName,
}: CartDrawerProps) {
  const isEmpty = cart.length === 0
  const canCheckout = !disabled && !isEmpty && outletName.trim().length > 0

  return (
    <>
      <div className="cart-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <aside className="cart-drawer open" role="dialog" aria-label="Keranjang Belanja" aria-modal="true">
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">Keranjang Belanja</h2>
          <button
            onClick={onClose}
            className="cart-drawer-close"
            aria-label="Tutup keranjang"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="cart-drawer-body">
          {isEmpty ? (
            <div className="cart-drawer-empty">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <p className="cart-drawer-empty-text">Keranjang kosong</p>
              <p className="cart-drawer-empty-hint">Tambah produk dari daftar untuk memulai</p>
            </div>
          ) : (
            <ul className="cart-drawer-items" role="list">
              {cart.map((item) => (
                <li key={`${item.productId}-${item.purchaseType}`} className="cart-drawer-item">
                  <div className="cart-drawer-item-info">
                    <p className="cart-drawer-item-name">{item.productName}</p>
                    <div className="cart-drawer-item-meta">
                      <span className="cart-drawer-item-type">{CUSTOMER_TYPE_LABELS[item.purchaseType]}</span>
                      <span className="cart-drawer-item-unit-price">
                        {formatRupiah(item.unitPrice)} / {CUSTOMER_TYPE_LABELS[item.purchaseType]}
                      </span>
                    </div>
                    <div className="cart-drawer-item-controls">
                      <button
                        type="button"
                        onClick={() => onUpdateQuantity(item.productId, item.purchaseType, -1)}
                        className="cart-drawer-qty-btn"
                        aria-label="Kurangi jumlah"
                        disabled={item.quantity <= 1}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="cart-drawer-qty-value" aria-label={`Jumlah: ${item.quantity}`}>{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => onUpdateQuantity(item.productId, item.purchaseType, 1)}
                        className="cart-drawer-qty-btn"
                        aria-label="Tambah jumlah"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="cart-drawer-item-right">
                    <span className="cart-drawer-item-subtotal">{formatRupiah(item.subtotal)}</span>
                    <button
                      onClick={() => onRemove(item.productId, item.purchaseType)}
                      className="cart-drawer-item-remove"
                      aria-label={`Hapus ${item.productName}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="cart-drawer-footer">
          <div className="cart-drawer-totals">
            <div className="cart-drawer-total-row">
              <span className="cart-drawer-total-label">Subtotal</span>
              <span className="cart-drawer-total-value">{formatRupiah(total)}</span>
            </div>
            <div className="cart-drawer-total-row cart-drawer-total-row--grand">
              <span className="cart-drawer-total-label">Total</span>
              <span className="cart-drawer-total-value">{formatRupiah(total)}</span>
            </div>
          </div>

          <div className="cart-drawer-actions">
            <button
              onClick={onCheckout}
              disabled={!canCheckout}
              className={`btn btn-primary btn-full cart-drawer-checkout ${!canCheckout ? 'disabled' : ''}`}
            >
              {disabled && outletName.trim().length === 0
                ? 'Isi Nama Warung / Toko Terlebih Dahulu'
                : isEmpty
                ? 'Keranjang Kosong'
                : 'Confirm Pembelian'}
            </button>
            <button
              onClick={onClose}
              className="btn btn-secondary btn-full cart-drawer-close-btn"
            >
              Tutup
            </button>
          </div>

          {!outletName.trim() && !isEmpty && (
            <p className="cart-drawer-warning">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Masukkan nama warung/toko di atas untuk melanjutkan ke konfirmasi
            </p>
          )}
        </div>
      </aside>
    </>
  )
}